import React, { useEffect, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import RideStatus from "./RideStatus";
import DetailsSidePanel from "./DetailsSidePanel";
import useDebounce from "../hooks/useDebounce";
import formatNumberShort from "../utils/formatNumberShort";
import axiosInstance from "../utils/axiosConfig";

import { convertTo24HourFormat } from "../utils/convertTimestampTo12HourFormat";
import { truncateWithEllipsis } from "../utils/removeSlugFromString";
import SearchActionComponent from "./reusableComponents/SearchActionComponent";
import SwipeableDrawerComponent from "./SwipeableDrawerComponent ";
import { BootstrapTooltip } from "./DetailsSidePanelChart";
import usePermission from "../hooks/usePermission";

import AddColumn from "./AddColumn";
import {
  customeDateHandler,
  customeTimeHandler,
} from "../const/commonfilterobject";
import CustomFilter, {
  SortPanel,
} from "./reusableComponents/Filter/CustomFilter";
import FilteredResults from "./FilteredResults";
import { showErrorToast } from "../utils/toastUtils";
import { removeQuery } from "../utils/queryHelpers";
import DataTable from "./DataTable";
import { bookingIdObj, commonSortOptions } from "../const/sort";
import useQueryParams from "../hooks/useQueryParams ";
import dayjs from "dayjs";
import CommonTableHead from "./CommonTableHead";
import { notifyToast } from "../utils/notify";
import { useSettings } from "../hooks/useSettings";

const TData = {
  columns: [
    { key: "cancelled_by", name: "Cancelled By" },
    {
      key: "ride_cancellation_reason",
      name: "RIDE Cancellation Reason",
    },
    {
      key: "unflagged_by",
      name: "Unflagged By",
    },
    {
      key: "unflagged_reason",
      name: "Unflagged Reason",
    },
  ],
};

const StatusIndicator = ({ index, text, fill }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: "12px",
    }}
  >
    <svg
      style={{
        borderRadius: "50%",
        ...(index === 0 && {
          boxShadow:
            "rgba(0, 0, 0, 0.02) 0px 1px 3px 0px, rgba(27, 31, 35, 0.15) 0px 0px 0px 1px",
        }),
      }}
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
    >
      <circle cx="10" cy="10" r="10" fill={fill} />
    </svg>

    <Typography>{text}</Typography>
  </Box>
);

// main component
const AllExecutiveRides = () => {
  const [isLoading, setisLoading] = useState(false);

  const location = useLocation();

  const [trips, setTrips] = useState([]);

  const { searchParams, setSearchParams, setQuery, getQuery } =
    useQueryParams();
  const rangeParam = searchParams.get("range");
  const [range, setRange] = useState(
    rangeParam || location?.state?.range || ""
  );
  const permissions = usePermission();
  // Loader state
  const [loader, setLoader] = useState(false);
  const { pathname } = useLocation();
  const [customerId, setCustomerId] = useState(location?.state?.rider_id || "");

  const toBoolean = (value) => {
    return value === "true";
  };

  const drawer = toBoolean(searchParams.get("drawer"));
  const id = searchParams.get("id");
  const [showDriverDetails, setDriverDetails] = useState({
    right: drawer || false,
  });
  const [driverId, setDriverId] = useState(id || "");
  const [showFilter, setShowFilter] = useState({ right: false });
  const [showCustomerFilter, setCustomerFilter] = useState({ right: false });
  // const [carType, setCarType] = useState(getQuery("car_type") ?? "");
  const rideStatusParam = searchParams.get("ride_status");
  const [rideStatus, setRideStatus] = useState(
    rideStatusParam ?? location?.state?.status ?? ""
  );
  
  const { dateformat, timezone, formatDate } = useSettings();
  const cancelledByParam = searchParams.get("cancelled_by");

  const [cancelledBy, setCancelledBy] = useState(
    cancelledByParam || location?.state?.cancelledBy || ""
  );
  const [bookingDay, setBookingDay] = useState(getQuery("booking_day") ?? "");
  const [date, setDate] = useState({
    startDate: getQuery("booking_date_gte")
      ? dayjs(getQuery("booking_date_gte"))
      : null,
    endDate: getQuery("booking_date_lte")
      ? dayjs(getQuery("booking_date_lte"))
      : null,
  });

  const [tripsFilter, setTripsFilter] = useState("");

  // dataGrid columns
  const columns = [
    {
      field: "",
      headerName: "",
      flex: 0.1,
      renderCell: (params) => <RideStatus {...params} />,
    },
    {
      field: "created_at",
      headerName: "Date",
      flex: 1,
      renderCell: (params) => {
        if (!params?.row?.created_at) {
          return "--";
        }
        return (
          <>
            <Button
              id={params.id}
              sx={{
                color: "#1D1F2C",
                fontFamily: "Proxima Nova",
                fontSize: "13px",
                letterSpacing: "0.26px",
                textTransform: "capitalize",
                border: "none",
              }}
            >
              {formatDate(params?.row?.created_at, timezone, dateformat)}
            </Button>
          </>
        );
      },
    },
    {
      field: "registration_number",
      headerName: "Plate Number",
      flex: 1,
      renderCell: (params) => {
        if (!params?.row?.registration_number) {
          return "--";
        }
        return (
          <>
            <Button
              id={params.id}
              sx={{
                color: "#1D1F2C",
                fontFamily: "Proxima Nova",
                fontSize: "13px",
                letterSpacing: "0.26px",
                textTransform: "capitalize",
                border: "none",
              }}
            >
              {params.row.registration_number}
            </Button>
          </>
        );
      },
    },
    {
      field: "driver_details.full_name",
      headerName: "Pilot Name",
      // flex: 0.6,
      renderCell: (params) => {
        if (!params?.row?.driver_details?.full_name) {
          return "--";
        }

        return (
          <>
            <BootstrapTooltip title={params?.row?.driver_details?.full_name}>
              <Button
                id={params.id}
                sx={{
                  border: "none",
                  display: "-webkit-box",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: "vertical",
                }}
              >
                <Typography
                  sx={{
                    color: "#1D1F2C",
                    fontFamily: "Proxima Nova",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    fontSize: "13px",
                    textTransform: "capitalize",
                    letterSpacing: "0.26px",
                    fontWeight: "500",
                  }}
                >
                  {params?.row?.driver_details?.full_name}
                </Typography>
              </Button>
            </BootstrapTooltip>
          </>
        );
      },
    },
    {
      field: "booking_id",
      headerName: "Booking ID",
      flex: 1,
      renderCell: (params) => {
        return (
          <>
            <Button
              id={params.id}
              sx={{
                color: "#043A87",
                fontFamily: "Proxima Nova",
                fontSize: "13px",
                letterSpacing: "0.26px",
                textTransform: "capitalize",
                border: "none",
              }}
              onClick={(e) => {
                e.stopPropagation();
                if (!params?.id) {
                  return;
                }
                const handleEvent = toggleDrawer("right", true);
                setDriverId(params?.id ?? "");
                setIdQueryParam(params?.id);
                handleEvent(e);
              }}
            >
              {params.row.booking_id}
            </Button>
          </>
        );
      },
    },
    {
      field: "pickup_location.address_text",
      headerName: "From",
      // flex: 0.6,
      renderCell: (params) => {
        if (!params?.row?.pickup_location?.address_text) {
          return "--";
        }

        return (
          <>
            <BootstrapTooltip
              title={params?.row?.pickup_location?.address_text}
            >
              <Button
                id={params.id}
                sx={{
                  border: "none",
                  display: "-webkit-box",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: "vertical",
                }}
              >
                <Typography
                  sx={{
                    color: "#1D1F2C",
                    fontFamily: "Proxima Nova",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    fontSize: "13px",
                    textTransform: "capitalize",
                    letterSpacing: "0.26px",
                    fontWeight: "500",
                  }}
                >
                  {params?.row?.pickup_location?.address_text}
                </Typography>
              </Button>
            </BootstrapTooltip>
          </>
        );
      },
    },
    {
      field: "dropoff_location.address_text",
      headerName: "To",
      // flex: 0.6,
      renderCell: (params) => {
        if (!params?.row?.dropoff_location?.address_text) {
          return "--";
        }

        return (
          <>
            <BootstrapTooltip
              title={params?.row?.dropoff_location?.address_text}
            >
              <Button
                id={params.id}
                sx={{
                  border: "none",
                  display: "-webkit-box",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: "vertical",
                }}
              >
                <Typography
                  sx={{
                    color: "#1D1F2C",
                    fontFamily: "Proxima Nova",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    fontSize: "13px",
                    textTransform: "capitalize",
                    letterSpacing: "0.26px",
                    fontWeight: "500",
                  }}
                >
                  {params?.row?.dropoff_location?.address_text}
                </Typography>
              </Button>
            </BootstrapTooltip>
          </>
        );
      },
    },
    {
      field: "rider_rating_review.review",
      headerName: "Remark",
      // flex: 0.6,
      renderCell: (params) => {
        if (!params?.row?.rider_rating_review?.review) {
          return "--";
        }

        return (
          <>
            <BootstrapTooltip title={params?.row?.rider_rating_review?.review}>
              <Button
                id={params.id}
                sx={{
                  border: "none",
                  display: "-webkit-box",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: "vertical",
                }}
              >
                <Typography
                  sx={{
                    color: "#1D1F2C",
                    fontFamily: "Proxima Nova",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    fontSize: "13px",
                    textTransform: "capitalize",
                    letterSpacing: "0.26px",
                    fontWeight: "500",
                  }}
                >
                  {params?.row?.rider_rating_review?.review}
                </Typography>
              </Button>
            </BootstrapTooltip>
          </>
        );
      },
    },
    {
      field: "price_breakup.base_fare",
      headerName: "Base Fare",
      // flex: 0.6,
      renderCell: (params) => {
        if (!params?.row?.price_breakup?.base_fare) {
          return "--";
        }

        return (
          <>
            <BootstrapTooltip title={params?.row?.price_breakup?.base_fare}>
              <Button
                id={params.id}
                sx={{
                  border: "none",
                  display: "-webkit-box",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: "vertical",
                }}
              >
                <Typography
                  sx={{
                    color: "#1D1F2C",
                    fontFamily: "Proxima Nova",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    fontSize: "13px",
                    textTransform: "capitalize",
                    letterSpacing: "0.26px",
                    fontWeight: "500",
                  }}
                >
                  {params?.row?.price_breakup?.base_fare}
                </Typography>
              </Button>
            </BootstrapTooltip>
          </>
        );
      },
    },
    {
      field: "initial_ride_distance_in_km",
      headerName: "Ride Distance (KM)",
      // flex: 0.6,
      renderCell: (params) => {
        if (!params?.row?.initial_ride_distance_in_km) {
          return "--";
        }

        return (
          <>
            <BootstrapTooltip title={params?.row?.initial_ride_distance_in_km}>
              <Button
                id={params.id}
                sx={{
                  border: "none",
                  display: "-webkit-box",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: "vertical",
                }}
              >
                <Typography
                  sx={{
                    color: "#1D1F2C",
                    fontFamily: "Proxima Nova",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    fontSize: "13px",
                    textTransform: "capitalize",
                    letterSpacing: "0.26px",
                    fontWeight: "500",
                  }}
                >
                  {params?.row?.initial_ride_distance_in_km}
                </Typography>
              </Button>
            </BootstrapTooltip>
          </>
        );
      },
    },
    {
      field: "price_breakup.distance_fee_per_km",
      headerName: "Distance Fee",
      // flex: 0.6,
      renderCell: (params) => {
        if (!params?.row?.price_breakup?.distance_fee_per_km) {
          return "--";
        }

        return (
          <>
            <BootstrapTooltip
              title={params?.row?.price_breakup?.distance_fee_per_km}
            >
              <Button
                id={params.id}
                sx={{
                  border: "none",
                  display: "-webkit-box",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: "vertical",
                }}
                onClick={() => {
                  handleNavigate(params?.row.driver_details?.id ?? "");
                }}
              >
                <Typography
                  sx={{
                    color: "#1D1F2C",
                    fontFamily: "Proxima Nova",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    fontSize: "13px",
                    textTransform: "capitalize",
                    letterSpacing: "0.26px",
                    fontWeight: "500",
                  }}
                >
                  ₱{params?.row?.price_breakup?.distance_fee_per_km}
                </Typography>
              </Button>
            </BootstrapTooltip>
          </>
        );
      },
    },
    {
      field: "initial_ride_duration_in_min",
      headerName: "Ride Time (Mins)",
      // flex: 0.6,
      renderCell: (params) => {
        return (
          <>
            <BootstrapTooltip title={params?.row?.initial_ride_duration_in_min}>
              <Button
                id={params.id}
                sx={{
                  border: "none",
                  display: "-webkit-box",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: "vertical",
                }}
                onClick={() => {
                  handleNavigate(params?.row.driver_details?.id ?? "");
                }}
              >
                <Typography
                  sx={{
                    color: "#1D1F2C",
                    fontFamily: "Proxima Nova",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    fontSize: "13px",
                    textTransform: "capitalize",
                    letterSpacing: "0.26px",
                    fontWeight: "500",
                  }}
                >
                  {params?.row?.initial_ride_duration_in_min}
                </Typography>
              </Button>
            </BootstrapTooltip>
          </>
        );
      },
    },
    {
      field: "price_breakup.ride_time_fee_per_min",
      headerName: "Time fee",
      // flex: 0.6,
      renderCell: (params) => {
        if (!params?.row?.price_breakup?.ride_time_fee_per_min) {
          return "--";
        }

        return (
          <>
            <BootstrapTooltip
              title={params?.row?.price_breakup?.ride_time_fee_per_min}
            >
              <Button
                id={params.id}
                sx={{
                  border: "none",
                  display: "-webkit-box",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: "vertical",
                }}
                onClick={() => {
                  handleNavigate(params?.row.driver_details?.id ?? "");
                }}
              >
                <Typography
                  sx={{
                    color: "#1D1F2C",
                    fontFamily: "Proxima Nova",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    fontSize: "13px",
                    textTransform: "capitalize",
                    letterSpacing: "0.26px",
                    fontWeight: "500",
                  }}
                >
                  ₱{params?.row?.price_breakup?.ride_time_fee_per_min}
                </Typography>
              </Button>
            </BootstrapTooltip>
          </>
        );
      },
    },
    {
      field: "price_breakup.surge_fee",
      headerName: "Surge",
      // flex: 0.6,
      renderCell: (params) => {
        if (!params?.row?.price_breakup?.surge_fee) {
          return "--";
        }

        return (
          <>
            <BootstrapTooltip title={params?.row?.price_breakup?.surge_fee}>
              <Button
                id={params.id}
                sx={{
                  border: "none",
                  display: "-webkit-box",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: "vertical",
                }}
                onClick={() => {
                  handleNavigate(params?.row.driver_details?.id ?? "");
                }}
              >
                <Typography
                  sx={{
                    color: "#1D1F2C",
                    fontFamily: "Proxima Nova",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    fontSize: "13px",
                    textTransform: "capitalize",
                    letterSpacing: "0.26px",
                    fontWeight: "500",
                  }}
                >
                  ₱{params?.row?.price_breakup?.surge_fee}
                </Typography>
              </Button>
            </BootstrapTooltip>
          </>
        );
      },
    },
    {
      field: "price_breakup.total_fare",
      headerName: "TOTAL FARE",
      // flex: 0.6,
      renderCell: (params) => {
        if (!params?.row?.price_breakup?.total_fare) {
          return "--";
        }

        return (
          <>
            <BootstrapTooltip title={params?.row?.price_breakup?.total_fare}>
              <Button
                id={params.id}
                sx={{
                  border: "none",
                  display: "-webkit-box",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: "vertical",
                }}
                onClick={() => {
                  handleNavigate(params?.row.driver_details?.id ?? "");
                }}
              >
                <Typography
                  sx={{
                    color: "#1D1F2C",
                    fontFamily: "Proxima Nova",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    fontSize: "13px",
                    textTransform: "capitalize",
                    letterSpacing: "0.26px",
                    fontWeight: "500",
                  }}
                >
                  ₱{params?.row?.price_breakup?.total_fare}
                </Typography>
              </Button>
            </BootstrapTooltip>
          </>
        );
      },
    },

    // {
    //   field: "ride_start_time",
    //   headerName: "Time",
    //   flex: 0.7,
    //   renderCell: (params) => {
    //     if (!params.value) {
    //       return "--";
    //     }

    //     let date = new Date(params.value);
    //     const hours = date.getHours();
    //     const minutes = date.getMinutes();
    //     const ampm = hours >= 12 ? "PM" : "AM";
    //     const hours12 = hours % 12 || 12;
    //     const formattedTime = `${hours12}:${
    //       (minutes < 10 ? "0" : "") + minutes
    //     } ${ampm}`;

    //     return (
    //       <Typography
    //         sx={{
    //           fontFamily: "Proxima Nova",
    //           fontSize: "13px",
    //           fontStyle: "normal",
    //           fontWeight: "400",
    //           lineHeight: "normal",
    //           letterSpacing: "0.26px",
    //         }}
    //       >
    //         {formattedTime}
    //       </Typography>
    //     );
    //   },
    // },
    // {
    //   field: "pickup_location.address_text",
    //   headerName: "From",
    //   flex: 1,
    //   renderCell: (param) => {
    //     // here we are taking row value from  params object
    //     if (!param.row.pickup_location.address_text) {
    //       return "--";
    //     }

    //     let pickeUpLoaction = param.row.pickup_location.address_text.replace(
    //       /,/g,
    //       " "
    //     );

    //     return (
    //       <Typography
    //         sx={{
    //           textOverflow: "ellipsis",
    //            width: "100%" ,
    //           height: "31px",
    //           overflow: "hidden",
    //           fontSize: "12px",
    //           fontStyle: "normal",
    //           fontFamily: "Proxima Nova",
    //           whiteSpace: "nowrap",
    //           fontWeight: "400",
    //           letterSpacing: "0.24px",
    //         }}
    //       >
    //         {pickeUpLoaction}
    //       </Typography>
    //     );
    //   },
    // },
    // {
    //   field: "dropoff_location.address_text",
    //   headerName: "To",
    //   flex: 1,
    //   renderCell: (param) => {
    //     if (!param.row.dropoff_location.address_text) {
    //       return "--";
    //     }

    //     let pickeUpLoaction = param.row.dropoff_location.address_text.replace(
    //       /,/g,
    //       " "
    //     );

    //     return (
    //       <Typography
    //         sx={{
    //           textOverflow: "ellipsis",
    //            width: "100%" ,
    //           height: "31px",
    //           overflow: "hidden",
    //           fontSize: "12px",
    //           fontStyle: "normal",
    //           fontFamily: "Proxima Nova",
    //           whiteSpace: "nowrap",
    //           fontWeight: "400",
    //           letterSpacing: "0.24px",
    //         }}
    //       >
    //         {pickeUpLoaction}
    //       </Typography>
    //     );
    //   },
    // },
  ];
  const [fieldsName, setFieldName] = useState(() => {
    return TData?.columns;
  });
  const [columnFields, setColumnFields] = useState(columns);

  const [search, setSearch] = useState("");
  const [rating, setRating] = useState(getQuery("rating") ?? "");
  const reasonParams = searchParams.get("reason");
  const [reason, setReason] = useState(
    reasonParams || location?.state?.reason || ""
  );

  const [review, setReview] = useState(getQuery("rating_and_reviews") ?? "");
  const [time, setTime] = useState({
    minTime: getQuery("booking_time_gte")
      ? dayjs(getQuery("booking_time_gte"))
      : "",
    maxTime: getQuery("booking_time_lte")
      ? dayjs(getQuery("booking_time_lte"))
      : "",
  });

  const [showSort, SetShowSort] = useState({ right: false });

  const [sort, setSort] = useState({
    bookingId: getQuery("booking_id_sort") ?? "",
    rating: getQuery("rating_sort") ?? "",
  });

  const specialFilter =
    pathname === "/customer-trips" ? "rider_ticket" : "driver_ticket";

  // Debounce
  const searchDebounce = useDebounce(search, 1500);

  // control mui pagination
  const page = parseInt(searchParams.get("page"), 0) || 0;
  const pageSize = parseInt(searchParams.get("limit"), 15) || 15;
  const [paginationModel, setPaginationModel] = React.useState({
    page: page || 0,
    pageSize: pageSize || 10,
  });

  // Pagination state
  const [totalCount, setTotalCount] = useState(0);

  const [activeButton, setActiveButton] = useState("Trips");

  const setIdQueryParam = (id) => {
    if (!id) return;
    searchParams.set("id", id);
    setSearchParams(searchParams, { replace: true });
  };

  const setDrawerQueryParam = (id) => {
    if (!id) return;
    searchParams.set("drawer", id);
    setSearchParams(searchParams, { replace: true });
  };

  const navigate = useNavigate();
  // mui toggleDrawer funtion for side panel
  const toggleDrawer = (anchor, open) => (event) => {
    if (!open) {
      setDriverId("");
    }
    if (
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }

    setDrawerQueryParam(open);
    setDriverDetails((prev) => ({ ...prev, [anchor]: open }));
  };

  const handlePaginationModel = (value) => {
    // Update the search parameters
    searchParams.set("page", value?.page || 0);
    searchParams.set("limit", value?.pageSize || 10);

    // Set the new search parameters with the replace option
    setSearchParams(searchParams, { replace: true });

    setPaginationModel((prev) => ({ ...prev, ...value }));
  };

  const toggleFilterDrawer = (anchor, open) => (event) => {
    if (
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }

    setShowFilter((prev) => ({ ...prev, [anchor]: open }));
  };

  const toggleSortDrawer = (anchor, open) => (event) => {
    if (
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }

    SetShowSort({ ...showSort, [anchor]: open });
  };

  const handleNavigate = (id) => {
    if (id) {
      navigate(`/driver/${id}`);
    }
  };
  // Function to handle click event by name
  const handleSelectNameClick = (name) => {
    setActiveButton(name);
  };

  const toggleCustomerFilterDrawer = (anchor, open) => (event) => {
    if (
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }

    setCustomerFilter((prev) => ({ ...prev, [anchor]: open }));
  };

  const checkQuery = (download = false) => {
    const query = [];

    if (pathname === "/customer-trips") {
      query.push(`is_rider_trip=${true}`);
    }

    if (reason) {
      searchParams.set("reason", reason);
      setSearchParams(searchParams, { replace: true });
      query.push(`ride_cancellation_reason=${reason}`);
    }

    if (download) {
      query.push(`is_download=${true}`);
    }

    if (location?.state?.special) {
      query.push(`special=${location.state.special}`);
    }

    if (range) {
      searchParams.set("range", range);
      setSearchParams(searchParams, { replace: true });
      query.push(`booking_day=${range}`);
    }

    if (rideStatus !== "") {
      if (location?.state?.status) {
        searchParams.set("ride_status", location?.state?.status);
        setSearchParams(searchParams, { replace: true });
      }

      query.push(
        `ride_status=${rideStatus || (location?.state?.status ?? "")}`
      );
    }

    if (bookingDay !== "" && bookingDay !== "Recent") {
      query.push(`booking_day=${bookingDay}`);
    }

    if (searchDebounce && search) {
      query.push(`search=${searchDebounce}`);
    }

    if (date.startDate && date.endDate) {
      let startDate = `${date.startDate.year()}-${
        date.startDate.month() + 1
      }-${date.startDate.date()}`;
      let endDate = `${date.endDate.year()}-${
        date.endDate.month() + 1
      }-${date.endDate.date()}`;
      query.push(`date_gte=${startDate}&date_lte=${endDate}`);
    }

    // if (carType !== "") {
    //   query.push(`car_type=${carType}`);
    // }

    if (rating !== "") {
      query.push(`rider_rating=${rating}`);
    }

    if (cancelledBy !== "") {
      if (location?.state?.cancelledBy) {
        setQuery("cancelled_by", cancelledBy, setSearchParams);
      }

      query.push(`cancelled_by=${cancelledBy}`);
    }

    if (review !== "") {
      query.push(`rider_rating_and_review=${review}`);
    }

    if (sort?.bookingId) {
      query.push(`booking_id_sort=${sort?.bookingId}`);
    }

    if (sort?.rating) {
      query.push(`rating_sort=${sort?.rating}`);
    }

    if (tripsFilter) {
      query.push(`trips_fare_sort=${tripsFilter}`);
    }

    if (time.minTime && time.maxTime) {
      query.push(
        `min_booking_time=${convertTo24HourFormat(
          time.minTime
        )}&max_booking_time=${convertTo24HourFormat(time.maxTime)}`
      );
    }

    if (customerId) {
      const riderId = customerId;
      query.push(`rider_id=${riderId}`);
    }

    let qurys = query.length > 1 ? query.join("&") : query.join("");

    return qurys;
  };

  // Funcion to Fetch Trips data from server
  const getTrips = async (qurys = []) => {
    setisLoading(true);
    let url =
      qurys.length > 0
        ? `/crm/executive-rides/?page=${paginationModel.page + 1}&limit=${
            paginationModel.pageSize
          }&${qurys}`
        : `/crm/executive-rides/?page=${paginationModel.page + 1}&limit=${
            paginationModel.pageSize
          }`;
    // passing token in headers

    try {
      // setLoader(true);
      await axiosInstance.get(url).then((res) => {
        setLoader(false);
        setTotalCount(res.data.data.total_count);
        setTrips(res?.data?.data?.records ?? []);
      });
    } catch (error) {
      setLoader(false);
      console.log("Error while Fetching Driver Trips", error);
    } finally {
      setisLoading(false);
    }
  };

  const handleColumnAdd = (checked, name, label) => {
    setColumnFields((prevFields) => {
      if (checked) {
        // If checked, add the field to the array

        if (name.includes("date")) {
          return [
            ...prevFields,
            {
              field: name,
              headerName: label,
              minWidth: 150,
              renderCell: (params) => {
                if (!params?.value) {
                  return "--";
                }

                const date = formatDate(params?.value);
                return <>{date}</>;
              },
            },
          ];
        } else {
          return [
            ...prevFields,
            {
              field: name,
              headerName: label,
              width: 150,
              renderCell: (params) => {
                if (!params?.value) {
                  return "--";
                }

                return <>{params.value}</>;
              },
            },
          ];
        }
      } else {
        // If unchecked, filter out the field from the array
        return prevFields.filter((field) => field.field !== name);
      }
    });
  };

  // Function
  const downloadFile = async () => {
    let qurys = checkQuery(true) ?? [];
    let url =
      qurys.length > 0
        ? `/crm/executive-rides/?page=${paginationModel.page + 1}&limit=${
            paginationModel.pageSize
          }&${qurys}`
        : `/crm/executive-rides/?page=${paginationModel.page + 1}&limit=${
            paginationModel.pageSizes
          }`;

    try {
      // setLoader(true);
      await axiosInstance.get(url).then((res) => {
        notifyToast.success(
          "Success",
          "The file has been successfully sent to your email. Please check your inbox.",
          {
            autoClose: 5000,
          }
        );
      });
    } catch (error) {
      setLoader(false);
      let message = error?.response?.data?.message;
      if (message) {
        showErrorToast(message);
      }
      console.log("Error while Fetching Driver Trips", error);
    }
  };

  // config

  const RideStatusConfig = [
    {
      text: "First Mile",
      fill: "#fff",
    },
    {
      text: "Ongoing Ride",
      fill: "#1D1F2C",
    },
    {
      text: "Completed Ride",
      fill: "#FFC940",
    },
    {
      text: "Cancelled Ride",
      fill: "#D1D1D1",
    },
    {
      text: "Driver Not Found",
      fill: "red",
    },
  ];

  // config
  const sub_section_to_label = {
    trips: "Trips",
  };

  const allowed_sub_sections = permissions?.SUB_SECTIONS ?? ["trips"];

  const tableHeadButtonConfig = allowed_sub_sections?.map((sub_section) => {
    return {
      label: sub_section_to_label[sub_section],
      value: "Trips",
      totalCount,
      onClick: handleSelectNameClick,
    };
  });

  const filterOptions = [
    {
      summryText: "Ride Status",
      state: rideStatus,
      filterName: "ride_status",
      setState: setRideStatus,
      options: [
        { label: "First Mile", value: "REQUESTED" },
        { label: "Ongoing", value: "ONGOING" },
        { label: "Completed", value: "COMPLETED" },
        { label: "Cancelled", value: "CANCELLED" },
        { label: "Driver Not Found", value: "DRIVER_NOT_FOUND" },
        { label: "Live Trips", value: "LIVE_TRIP" },
      ],
    },
    {
      summryText: "Cancelled By",
      filterName: "cancelled_by",
      state: cancelledBy,
      setState: setCancelledBy,
      options: [
        { label: "Rider", value: "RIDER" },
        { label: "Driver", value: "DRIVER" },
      ],
    },
    {
      summryText: "Booking Day",
      filterName: "booking_day",
      state: bookingDay,
      setState: setBookingDay,
      options: [
        { label: "Today", value: "today" },
        { label: "Last week", value: "last_week" },
        { label: "Yesterday", value: "yesterday" },
        { label: "Last month", value: "last_month" },
        { label: "Last year", value: "last_year" },
      ],
      date: {
        customDateState: date,
        setState: setDate,
        customeDateHandler,
        customDateHeader: "Custom",
        filterName: ["booking_date_gte", "booking_date_lte"],
        customDateLabel: ["From", "To"],
        customDateName: ["startDate", "endDate"],
      },
    },
    {
      summryText: "Booking time",
      time: {
        timeLabel: ["Min Time", "Max Time"],
        filterName: ["booking_time_gte", "booking_time_lte"],
        customTimeName: ["minTime", "maxTime"],
        timeState: time,
        setState: setTime,
        customeTimeHandler,
      },
    },
    // {
    //   summryText: "Car Type",
    //   filterName: "car_type",
    //   state: carType,
    //   setState: setCarType,
    //   options: [
    //     { value: "COMPACT", label: "Compact" },
    //     { value: "TAXI", label: "Taxi" },
    //     { value: "PLUS", label: "Plus" },
    //     { value: "EXECUTIVE", label: "Executive" },
    //     // { value: "LUXURY", label: "Luxury" }
    //   ],
    // },
    // {
    //   summryText: "Rating",
    //   filterName: "rating",
    //   state: rating,
    //   setState: setRating,
    //   options: [
    //     { value: "5", label: "5 star" },
    //     { value: "4", label: "4 star" },
    //     { value: "3", label: "3 star" },
    //     { value: "2", label: "2 star" },
    //     { value: "1", label: "1 star" },
    //   ],
    // },
    // {
    //   summryText: "Rating & Reviews",
    //   filterName: "rating_and_reviews",
    //   state: review,
    //   setState: setReview,
    //   options: [
    //     { value: "rating_only", label: "Rating only" },
    //     { value: "review_only", label: "Review only" },
    //     { value: "rating_and_review_both", label: "Rating and review both" },
    //     { value: "no_rating_and_review", label: "No rating and review" },
    //   ],
    // },
    ,
    {
      summryText: "Range",
      state: range,
      ignorefilter: true,
      filterName: "range",
      setState: setRange,
      options: [
        {
          label: "Today",
          value: "today",
        },
        {
          label: "Yesterday",
          value: "yesterday",
        },
        {
          label: "Last Week",
          value: "WEEKLY",
        },
        {
          label: "Last Month",
          value: "MONTHLY",
        },
      ],
    },
    {
      summryText: "Rider Id",
      filterName: "rider id",
      ignorefilter: true,
      state: customerId,
      setState: setCustomerId,
      options: [
        {
          label: customerId,
          value: customerId,
        },
      ],
    },
    {
      summryText: "Reason",
      filterName: "reason",
      ignorefilter: true,
      state: reason,
      setState: setReason,
      options: [],
    },
    // {
    //   summryText: "Range",
    //   state: review,
    //   setState: setReview,
    //   options: [
    //     { value: "rating_only", label: "Rating only" },
    //     { value: "review_only", label: "Review only" },
    //     { value: "rating_and_review_both", label: "Rating and review both" },
    //     { value: "no_rating_and_review", label: "No rating and review" },
    //   ],
    // },
    // {
    //   summaryText: "Trip Fare",
    //   state: trips,
    //   setState: setTrips,
    //   options: [
    //     { value: "min", label: "Minimum" },
    //     { value: "max", label: "Maximum" },
    //   ],
    // },
  ];

  const sortFilterConfig = {
    options: [
      {
        ...bookingIdObj,
        state: sort?.bookingId ?? [],
        filterName: "booking_id_sort",
      },
      {
        summaryText: "Rating",
        name: "rating",
        filterName: "rating_sort",
        state: sort?.rating ?? [],
        options: commonSortOptions,
      },
    ],
    handleClearState: () => {},
    // handleFilterState: handleFilterState,
  };

  const buttonConfig = {
    buttonConfig: tableHeadButtonConfig,
    active: activeButton,
    totalcount: totalCount,
  };

  useEffect(() => {
    setFieldName(TData?.columns);
  }, []);

  useEffect(() => {
    if (["RIDER", "DRIVER"].includes(cancelledBy)) {
      TData?.columns.forEach((res) => {
        handleColumnAdd(true, res.key, res.name);
      });
    }
  }, []);

  // useEffect(() => {
  //   console.log(location?.state);
  //   if (location?.state && Object.entries(location.state).length > 0) {
  //     if (!rideStatus && location?.state?.status) {
  //       setRideStatus(location?.state?.rideStatus);
  //     }
  //   }
  // }, []);

  useEffect(() => {
    let qurys = checkQuery();

    getTrips(qurys);
  }, [
    rideStatus,
    bookingDay,
    date,
    // carType,
    paginationModel.page,
    paginationModel.pageSize,
    searchDebounce,
    review,
    rating,
    tripsFilter,
    time.minTime,
    time.maxTime,
    cancelledBy,
    range,
    reason,
    customerId,
    sort,
  ]);

  return (
    <>
      {loader ? (
        <div>loading..</div>
      ) : (
        <Box
          sx={{
            margin: "20px",
          }}
        >
          <SearchActionComponent
            value={search}
            setState={setSearch}
            toggleDrawer={toggleFilterDrawer}
            placeholder={"Search Customer , Driver, Booking ID"}
            toggleSortDrawer={toggleSortDrawer}
            require={{
              search: true,
              filter: true,
              download: pathname !== "/customer-trips" ? true : false,
              sort: false,
            }}
            downloadFile={downloadFile}
          >
            {/* <AddColumn
              fieldsName={fieldsName}
              handleColumnAdd={handleColumnAdd}
              columnFields={columnFields}
            /> */}
          </SearchActionComponent>

          <FilteredResults options={filterOptions} />

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <CommonTableHead
              totalcount={buttonConfig?.totalcount}
              ButtonConfig={tableHeadButtonConfig}
              active={buttonConfig?.active}
            />
          </Box>

          <DataTable
            row={trips || []}
            columns={columns}
            sx={{ cursor: "pointer" }}
            paginationModel={paginationModel}
            loading={isLoading}
            setPaginationModel={setPaginationModel}
            totalCount={Math.ceil(totalCount)}
          />

          {showDriverDetails["right"] && (
            <SwipeableDrawerComponent
              isOpen={showDriverDetails["right"]}
              anchor="right"
              onClose={(e) => {
                removeQuery("drawer", setSearchParams);
                removeQuery("id", setSearchParams);
                toggleDrawer("right", false)(e);
              }}
            >
              <DetailsSidePanel
                setDriverId={setDriverId}
                DId={driverId}
                anchor={"right"}
                role={pathname === "/customer-trips" ? "customer" : ""}
                specialFilter={specialFilter}
                toggleDrawer={toggleDrawer}
              />
            </SwipeableDrawerComponent>
          )}

          {showFilter && (
            <SwipeableDrawerComponent
              isOpen={showFilter["right"]}
              anchor="right"
              onClose={toggleFilterDrawer("right", false)}
            >
              <CustomFilter
                filterOptions={filterOptions}
                toggleDrawer={() => toggleFilterDrawer("right", false)}
              />
            </SwipeableDrawerComponent>
          )}

          {showCustomerFilter["right"] && (
            <SwipeableDrawerComponent
              isOpen={showCustomerFilter["right"]}
              anchor="right"
              onClose={toggleCustomerFilterDrawer("right", false)}
            >
              <DetailsSidePanel
                DId={driverId}
                anchor={"right"}
                toggleDrawer={toggleCustomerFilterDrawer}
                role="customer"
                specialFilter={"rider_ticket"}
                fetchFunction={getTrips}
              />
            </SwipeableDrawerComponent>
          )}

          {showSort.right && (
            <SwipeableDrawerComponent
              isOpen={showSort["right"]}
              onClose={toggleSortDrawer("right", false)}
              anchor="right"
            >
              <SortPanel
                toggleDrawer={toggleSortDrawer}
                sortOptions={sortFilterConfig?.options}
                // handleClear={handleClear}
                setState={setSort}
              />
            </SwipeableDrawerComponent>
          )}

          <Box
            sx={{
              width: "auto",
              margin: "auto",
              padding: "25px 0",
            }}
          >
            <Typography>Ride Status</Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                marginTop: "12px",
              }}
            >
              {RideStatusConfig.length > 0 &&
                RideStatusConfig.map((res, index) => (
                  <StatusIndicator
                    key={res.text}
                    index={index}
                    text={res.text}
                    fill={res.fill}
                  />
                ))}
            </Box>
          </Box>
        </Box>
      )}
    </>
  );
};

export default AllExecutiveRides;
